import { redact, setLogSink, type LogEntry } from '../lib/logger.js';
import { db } from './db.js';

/**
 * Registro de incidencias que sobrevive al reinicio de la aplicacion.
 *
 * El anillo del `logger` vive en memoria, asi que en Android bastaba con que el
 * sistema matara el proceso para perder el motivo de un fallo. Aqui se guardan
 * los avisos y errores —solo esos— en la tabla `meta`, que es LOCAL y no se
 * sincroniza nunca: son datos de diagnostico de este dispositivo, no del
 * usuario, y no tienen por que viajar a ningun servidor.
 *
 * En `meta` y no en una tabla propia porque no hace falta consultarlos por
 * ningun campo: se leen enteros y se escriben enteros. Una tabla nueva
 * obligaria a subir la version de Dexie sin ganar nada.
 *
 * Lo que se guarda va REDACTADO (docs/SECURITY_PRIVACY.md §7): en cuanto sale
 * de la memoria del proceso puede acabar en una exportacion o en una captura
 * de pantalla.
 */

const CLAVE = 'incidencias';

/** Cuantas se conservan. Interesa el pasado reciente, no el historial. */
const MAXIMO = 50;

export async function leerIncidencias(): Promise<LogEntry[]> {
  const fila = await db.meta.get(CLAVE);
  return Array.isArray(fila?.value) ? (fila.value as LogEntry[]) : [];
}

export async function vaciarIncidencias(): Promise<void> {
  await db.meta.put({ key: CLAVE, value: [] });
}

// Las escrituras se encadenan: dos errores seguidos harian dos ciclos
// leer-modificar-escribir solapados y el segundo perderia al primero.
let cola: Promise<void> = Promise.resolve();
let escribiendo = false;

function persistir(entry: LogEntry): void {
  if (entry.level !== 'warn' && entry.level !== 'error') return;
  // Un fallo registrado DESDE la propia escritura entraria en bucle.
  if (escribiendo) return;

  const guardado: LogEntry = {
    at: entry.at,
    level: entry.level,
    domain: entry.domain,
    code: entry.code,
    message: entry.message,
  };
  if (entry.data !== undefined) {
    guardado.data = redact(entry.data) as Record<string, unknown>;
  }

  cola = cola.then(async () => {
    escribiendo = true;
    try {
      const previas = await leerIncidencias();
      await db.meta.put({ key: CLAVE, value: [...previas, guardado].slice(-MAXIMO) });
    } catch {
      // Si no se puede escribir, el aviso sigue en el anillo de memoria. No hay
      // nada mejor que hacer y quejarse aqui volveria a llamar al `logger`.
    } finally {
      escribiendo = false;
    }
  });
}

/** Engancha el registro persistente. Se llama una vez, al arrancar. */
export function instalarRegistroPersistente(): void {
  setLogSink(persistir);
}

/** Solo para tests: espera a que la cola de escrituras se vacie. */
export function escrituraPendiente(): Promise<void> {
  return cola;
}
